import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import { Container, Row, Col, Card, Badge, Alert, ProgressBar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faServer, 
  faCheckCircle, 
  faCloud, 
  faNetworkWired, 
  faShieldAlt, 
  faMicrochip, 
  faTerminal, 
  faCodeBranch
} from '@fortawesome/free-solid-svg-icons';
import AwsChatbot from '../AwsChatbot';
import AndroidAppFlavours from '../shared/AndroidAppFlavours';
import packageJson from '../../../package.json';

const SystemMonitorPage = () => {
  const { authState } = useOktaAuth();
  const location = useLocation();

  // Extract monitored user name or identifier from query param (?monitor=...)
  const searchParams = new URLSearchParams(location.search);
  const monitorUser = searchParams.get('monitor') || 'Pro Client';
  const isAuthenticated = Boolean(authState?.isAuthenticated);

  return (
    <div className="system-monitor-page">
      {/* Standard Hero Header */}
      <header className="justify-content-center py-3 bg-warning text-center text-white shadow-sm mb-4">
        <div className="container">
          <div className="row">
            <div className="col">
              <div className="d-flex align-items-center justify-content-center gap-2 mb-1">
                <FontAwesomeIcon icon={faCloud} className="fs-3" />
                <h1 className="font-weight-bold mb-0">
                  SystemDash Platform Monitor | Pro Cloud Infrastructure
                </h1>
              </div>
              <p className="mb-0 text-white" style={{ opacity: 0.9 }}>
                Cloud infrastructure health, operational status, and automated deployment observability (v{packageJson.version})
              </p>
            </div>
          </div>
        </div>
      </header>

      <Container className="pb-5">
        {/* Top Status & Monitored Target Banner */}
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4 bg-white p-3 rounded-3 shadow-sm border">
          <div>
            <span className="text-muted small d-block">Monitored Target Environment:</span>
            <strong className="fs-5 text-dark">
              {monitorUser}
            </strong>
            <Badge bg="warning" text="dark" className="ms-2 fw-bold">
              PRO Tier Provisioned
            </Badge>
            <Badge bg={isAuthenticated ? "primary" : "secondary"} className="ms-2">
              {isAuthenticated ? "Live Session" : "Demo Preview"}
            </Badge>
          </div>
          <div className="d-flex align-items-center gap-2">
            <span className="badge bg-success-subtle text-success border border-success px-3 py-2 fs-6">
              <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
              Cloud Infrastructure Healthy &amp; Operational
            </span>
          </div>
        </div>

        {/* Primary Health & Node Status Grid */}
        <Row className="g-4 mb-4">
          <Col lg={4} md={6}>
            <Card className="h-100 shadow-sm border rounded-3">
              <Card.Header className="bg-light d-flex justify-content-between align-items-center py-2 px-3">
                <strong className="text-dark small">
                  <FontAwesomeIcon icon={faServer} className="text-primary me-2" />
                  Provisioned Cluster
                </strong>
                <Badge bg="success">Operational</Badge>
              </Card.Header>
              <Card.Body className="p-3">
                <p className="small text-muted mb-2">
                  Dedicated virtual compute node allocated for Pro user telemetry ingestion and secure isolation.
                </p>
                <ul className="list-unstyled small mb-0">
                  <li className="mb-1 d-flex justify-content-between">
                    <span>Instance State:</span>
                    <strong className="text-success">Running</strong>
                  </li>
                  <li className="mb-1 d-flex justify-content-between">
                    <span>Region:</span>
                    <strong className="text-dark">eu-central-1</strong>
                  </li>
                  <li className="mb-1 d-flex justify-content-between">
                    <span>Cluster Ingress:</span>
                    <strong className="text-dark">TLS 1.3 · IAM Protected</strong>
                  </li>
                  <li className="d-flex justify-content-between">
                    <span>Node Uptime:</span>
                    <strong className="text-dark">99.98% (Healthy)</strong>
                  </li>
                </ul>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4} md={6}>
            <Card className="h-100 shadow-sm border rounded-3">
              <Card.Header className="bg-light d-flex justify-content-between align-items-center py-2 px-3">
                <strong className="text-dark small">
                  <FontAwesomeIcon icon={faNetworkWired} className="text-warning me-2" />
                  Telemetry Pipeline
                </strong>
                <Badge bg="info" text="white">Synced</Badge>
              </Card.Header>
              <Card.Body className="p-3">
                <p className="small text-muted mb-2">
                  High-throughput event queue buffering wearable signals before persistence in S3 and RDS.
                </p>
                <div className="mb-2">
                  <div className="d-flex justify-content-between small mb-1">
                    <span>Queue Saturation:</span>
                    <span className="fw-bold text-success">18% Capacity</span>
                  </div>
                  <ProgressBar variant="success" now={18} style={{ height: '6px' }} />
                </div>
                <div className="mb-2">
                  <div className="d-flex justify-content-between small mb-1">
                    <span>Telemetry Latency:</span>
                    <span className="fw-bold text-dark">24 ms</span>
                  </div>
                  <ProgressBar variant="info" now={24} style={{ height: '6px' }} />
                </div>
                <div className="small text-muted border-top pt-2">
                  Data validation: <strong>Zero packet loss</strong>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4} md={12}>
            <Card className="h-100 shadow-sm border rounded-3">
              <Card.Header className="bg-light d-flex justify-content-between align-items-center py-2 px-3">
                <strong className="text-dark small">
                  <FontAwesomeIcon icon={faShieldAlt} className="text-success me-2" />
                  Security &amp; Encryption
                </strong>
                <Badge bg="dark">Enforced</Badge>
              </Card.Header>
              <Card.Body className="p-3">
                <p className="small text-muted mb-2">
                  Managed Service Control Policies (SCP) &amp; Resource Control Policies (RCP) governance.
                </p>
                <ul className="list-unstyled small mb-0">
                  <li className="mb-1 d-flex justify-content-between">
                    <span>IAM DB Auth:</span>
                    <span className="text-success fw-bold">Active</span>
                  </li>
                  <li className="mb-1 d-flex justify-content-between">
                    <span>KMS Key Rotation:</span>
                    <span className="text-dark">AWS Managed</span>
                  </li>
                  <li className="d-flex justify-content-between">
                    <span>VPC Endpoint Access:</span>
                    <span className="text-primary fw-bold">PrivateLink Only</span>
                  </li>
                </ul>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Future Architecture: Monitoring Slave & DSV libcontimig Topology */}
        <Card className="shadow-sm border rounded-3 mb-4">
          <Card.Header className="bg-white border-bottom py-3">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2">
                <FontAwesomeIcon icon={faMicrochip} className="text-warning fs-5" />
                <h5 className="fw-bold mb-0 text-dark">
                  Monitoring Slave Telemetry &amp; DSV Infrastructure Topology (TO-BE)
                </h5>
              </div>
              <Badge bg="secondary">In Development (Phase 2)</Badge>
            </div>
          </Card.Header>
          <Card.Body className="p-4">
            <Row className="g-4 align-items-center">
              <Col lg={7} md={12}>
                <h6 className="fw-bold text-dark mb-2">
                  Dual-Source HealthDash Observability Ingestion
                </h6>
                <p className="text-muted small mb-3">
                  This monitoring portal is designed to ingest operational telemetry from two complementary channels
                  for Pro and Premium workloads:
                </p>
                <ul className="small text-muted mb-3">
                  <li className="mb-2">
                    <strong>Direct Monitoring Slave:</strong> A lightweight telemetry monitoring daemon deployed 
                    directly on the provisioned HealthDash infrastructure in INSTANCE reports real-time CPU, memory, 
                    disk, and network metrics to verify healthy and operational state.
                  </li>
                  <li className="mb-2">
                    <strong>Infrastructure Topology using DSV:</strong> Operational telemetry can also be 
                    streamed via the previously generated infrastructure topology created using 
                    <strong> Data Source Visualizer (DSV)</strong>, dynamically mapping wearable 
                    client endpoints (Samsung Health, Huawei Health, Oura) through ingress relays to the instances.
                  </li>
                </ul>
                <div className="p-3 bg-light rounded-2 border small">
                  <span className="text-dark fw-bold">
                    <FontAwesomeIcon icon={faCodeBranch} className="me-1 text-warning" />
                    Pro &amp; Premium Health Drilldown Integration:
                  </span>{' '}
                  Health data captured from all wearable sources supported by Android apps
                  will provide full drilldown analysis, with upcoming role-based gating and deep links to the centralized 
                  <strong> Admin Panel </strong>.
                </div>
              </Col>
              <Col lg={5} md={12}>
                <div className="border rounded-3 p-3 bg-light text-center">
                  <div className="text-muted small fw-bold mb-2">Simulated Live Ingestion Stream</div>
                  <div className="bg-dark text-success p-3 rounded text-start font-monospace small mb-2" style={{ fontSize: '0.78rem', minHeight: '140px' }}>
                    <div>&gt; [INSTANCE] Daemon heartbeat: OK (PID 2840)</div>
                    <div>&gt; [INSTANCE] Latency probe: 18.4ms (Healthy)</div>
                    <div>&gt; [INSTANCE] Topology graph: 4 nodes synchronized</div>
                    <div>&gt; [INSTANCE] Ingress routes: active </div>
                    <div className="text-warning">&gt; [STATUS] Infrastructure operational</div>
                  </div>
                  <span className="badge bg-success">Stream Connected</span>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Developer & DevOps Notice: CloudFormation EC2 Instance */}
        <Alert variant="info" className="shadow-sm border-0 mb-4 p-4 rounded-3">
          <div className="d-flex align-items-start gap-3">
            <div className="fs-3 text-info">
              <FontAwesomeIcon icon={faTerminal} />
            </div>
            <div>
              <h5 className="alert-heading fw-bold mb-1">
                Developer &amp; DevOps Infrastructure Notice: CloudFormation Instance
              </h5>
              <p className="small mb-2 text-dark">
                The automated deployment and infrastructure stack lifecycle for Pro instances is orchestrated via the 
                <strong> CloudFormation instance</strong>. This instance executes automated stack definitions, executes 
                initialization hooks (UserData / cfn-init), and attaches the monitoring slave daemon to the provisioned cluster.
              </p>
              <p className="small text-muted mb-0">
                <em>Note for developers:</em> Direct console links and privileged API endpoints to the CloudFormation 
                instance are deliberately withheld in this frontend release for IAM least-privilege compliance and will 
                be published through authenticated backoffice tooling.
              </p>
            </div>
          </div>
        </Alert>

        {/* Companion Links */}
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
          <Link to="/profile" className="btn btn-outline-warning fw-bold">
            &larr; Return to User Profile
          </Link>
          <div className="d-flex gap-2">
            <Link to="/user" className="btn btn-outline-dark fw-bold">
              Open Dashboard
            </Link>
            <Link to="/ecosystem/PCD" className="btn btn-warning text-white fw-bold shadow-sm">
              Open PCD Ecosystem
            </Link>
          </div>
        </div>

        {/* Android Companion Flavours (libhacontimig) */}
        <Card className="border-0 shadow-sm rounded-3 mb-4 p-3 bg-light">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <span className="fw-bold small text-secondary">HealthySync and HealthyMe</span>
            <Badge bg="warning" text="dark" className="small">PRO Capable</Badge>
          </div>
          <AndroidAppFlavours defaultOpen={false} />
        </Card>

        <AwsChatbot />
      </Container>
    </div>
  );
};

export default SystemMonitorPage;
